import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Slider,
    Stack,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { alpha } from '@mui/material/styles';
import rrwebPlayer from 'rrweb-player';
import 'rrweb-player/dist/style.css';
import * as Sentry from '@sentry/react';
import {
    loadSessionReplayFromChunkUrls,
    REPLAY_FORMAT_RRWEB,
} from 'utils/assignmentSessionReplayLoad';
import { sanitizeSnapshotHtml } from 'utils/assignmentSessionReplay';

const TEAL = '#03989e';

function formatMs(ms) {
    const totalSec = Math.max(0, Math.floor(Number(ms) / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

function buildSnapshotSrcDoc(html, viewport) {
    const w = viewport?.w || 1280;
    const h = viewport?.h || 720;
    const safe = sanitizeSnapshotHtml(html || '');
    const wrap = 'div';
    return `<!DOCTYPE html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=${w}"/><style>
      html,body{margin:0;padding:0;background:#f4f6f8;overflow:auto;}
      .owe-replay-root{min-height:${h}px;box-sizing:border-box;}
      *{pointer-events:none!important;}
    </style></head><body><${wrap} class="owe-replay-root">${safe}</${wrap}></body></html>`;
}

function LegacyAssignmentSessionReplayPlayer({ events, viewport }) {
    const frameRef = useRef(null);
    const overlayRef = useRef(null);
    const rafRef = useRef(null);
    const playStartRef = useRef(0);
    const playheadRef = useRef(0);
    const eventIndexRef = useRef(0);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [playhead, setPlayhead] = useState(0);
    const duration = events.length ? events[events.length - 1].t || 0 : 0;

    const applyEvent = useCallback((event) => {
        const iframe = frameRef.current;
        if (!iframe) return;
        if (event.type === 'snapshot' && event.html) {
            iframe.srcdoc = buildSnapshotSrcDoc(event.html, viewport);
            return;
        }
        if (event.type === 'scroll') {
            try {
                iframe.contentWindow?.scrollTo(event.x || 0, event.y || 0);
            } catch {
            }
            return;
        }
        if (event.type === 'click' && overlayRef.current) {
            const dot = document.createElement('div');
            dot.style.cssText = `position:absolute;left:${event.x}px;top:${event.y}px;width:18px;height:18px;margin:-9px 0 0 -9px;border-radius:50%;background:${alpha(TEAL, 0.35)};border:2px solid ${TEAL};pointer-events:none;transition:opacity .4s;`;
            overlayRef.current.appendChild(dot);
            window.setTimeout(() => dot.remove(), 500);
        }
    }, [viewport]);

    const seekTo = useCallback(
        (ms, autoPlay) => {
            eventIndexRef.current = 0;
            playheadRef.current = ms;
            setPlayhead(ms);
            const iframe = frameRef.current;
            if (iframe) iframe.srcdoc = '';
            for (let i = 0; i < events.length; i += 1) {
                if ((events[i].t ?? 0) <= ms) {
                    applyEvent(events[i]);
                    eventIndexRef.current = i + 1;
                } else break;
            }
            if (autoPlay) setPlaying(true);
        },
        [events, applyEvent]
    );

    useEffect(() => {
        if (!events.length) return;
        seekTo(0, false);
    }, [events, seekTo]);

    useEffect(() => {
        if (!playing) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            return undefined;
        }
        playStartRef.current = performance.now() - playheadRef.current / speed;
        const tick = () => {
            const nextPlayhead = (performance.now() - playStartRef.current) * speed;
            playheadRef.current = nextPlayhead;
            setPlayhead(nextPlayhead);
            while (eventIndexRef.current < events.length) {
                const ev = events[eventIndexRef.current];
                if ((ev.t ?? 0) > nextPlayhead) break;
                applyEvent(ev);
                eventIndexRef.current += 1;
            }
            if (nextPlayhead >= duration) {
                setPlaying(false);
                return;
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [playing, speed, events, duration, applyEvent]);

    return (
        <Box>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '16 / 9',
                    bgcolor: '#1a2b3c',
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <Box
                    component="iframe"
                    ref={frameRef}
                    title="Assignment session replay"
                    sandbox="allow-same-origin"
                    sx={{
                        width: '100%',
                        height: '100%',
                        border: 0,
                        bgcolor: '#fff',
                    }}
                />
                <Box
                    ref={overlayRef}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                    }}
                />
            </Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1.5, px: 0.5 }}>
                <IconButton
                    size="small"
                    onClick={() => setPlaying((p) => !p)}
                    disabled={!duration}
                    sx={{ color: TEAL }}
                >
                    {playing ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
                <Typography variant="caption" sx={{ minWidth: 40, color: '#5c6b7a' }}>
                    {formatMs(playhead)}
                </Typography>
                <Slider
                    size="small"
                    min={0}
                    max={duration || 1}
                    value={Math.min(playhead, duration || 0)}
                    onChange={(_, v) => seekTo(v, false)}
                    sx={{ flex: 1, color: TEAL }}
                />
                <Typography variant="caption" sx={{ minWidth: 40, color: '#5c6b7a' }}>
                    {formatMs(duration)}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 72 }}>
                    <Select value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
                        <MenuItem value={0.5}>0.5x</MenuItem>
                        <MenuItem value={1}>1x</MenuItem>
                        <MenuItem value={2}>2x</MenuItem>
                        <MenuItem value={4}>4x</MenuItem>
                    </Select>
                </FormControl>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Older recording format — limited playback. New student sessions use full replay.
            </Typography>
        </Box>
    );
}

function RrwebAssignmentSessionReplayPlayer({ events, width, height }) {
    const containerRef = useRef(null);
    const playerRef = useRef(null);

    useEffect(() => {
        if (!events?.length || !containerRef.current) return undefined;
        containerRef.current.innerHTML = '';
        try {
            const player = new rrwebPlayer({
                target: containerRef.current,
                props: {
                    events,
                    width,
                    height,
                    autoPlay: false,
                    showController: true,
                    speedOption: [0.5, 1, 2, 4, 8],
                    maxScale: 1,
                },
            });
            playerRef.current = player;
        } catch (error) {
            Sentry.captureException(error);
        }
        return () => {
            try {
                playerRef.current?.$destroy?.();
            } catch {
            }
            playerRef.current = null;
            if (containerRef.current) containerRef.current.innerHTML = '';
        };
    }, [events, width, height]);

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                '& .rr-player': {
                    margin: '0 auto',
                    boxShadow: `0 12px 40px ${alpha('#000', 0.12)}`,
                },
            }}
        />
    );
}

export default function AssignmentSessionReplayPlayer({ chunkUrls, title }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [merged, setMerged] = useState(null);
    const [playerSize, setPlayerSize] = useState({ width: 960, height: 540 });
    const wrapRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        setMerged(null);
        loadSessionReplayFromChunkUrls(chunkUrls)
            .then((data) => {
                if (cancelled) return;
                if (!data?.events?.length) {
                    setError('This recording has no playable events.');
                    return;
                }
                setMerged(data);
            })
            .catch((err) => {
                if (cancelled) return;
                Sentry.captureException(err);
                setError(err?.message || 'Failed to load recording.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [chunkUrls]);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const update = () => {
            const w = Math.min(el.clientWidth || 960, 1024);
            setPlayerSize({ width: Math.floor(w), height: Math.floor((w * 9) / 16) });
        };
        update();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        if (ro) ro.observe(el);
        else window.addEventListener('resize', update);
        return () => {
            if (ro) ro.disconnect();
            else window.removeEventListener('resize', update);
        };
    }, [merged]);

    if (loading) {
        return (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress sx={{ color: TEAL }} />
                <Typography color="text.secondary">Loading session replay…</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box ref={wrapRef}>
            {title ? (
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#1a2b3c' }}>
                    {title}
                </Typography>
            ) : null}
            {merged.format === REPLAY_FORMAT_RRWEB ? (
                <RrwebAssignmentSessionReplayPlayer
                    events={merged.events}
                    width={playerSize.width}
                    height={playerSize.height}
                />
            ) : (
                <LegacyAssignmentSessionReplayPlayer events={merged.events} viewport={merged.viewport} />
            )}
        </Box>
    );
}
